import dayjs from 'dayjs'
import { makeAutoObservable } from 'mobx'

import type IUiStore from '@src/app/IUiStore'
import StatefulPromise from '@src/lib/StatefulPromise'
import { DisposeBag } from '@src/lib/dispose'
import { logError } from '@src/lib/log'
import type AiStore from '@src/service/AiStore/AiStore'
import type { AnalyticsStore } from '@src/service/analytics'
import Collection from '@src/service/collections/Collection'
import type { IFlagsService } from '@src/service/feature-flags/FlagsService'
import type Transport from '@src/service/transport'
import type {
  AiCustomJobDefinitionV1CreateInput,
  AiCustomJobDefinitionV1UpdateInput,
  CodableAiCustomJobV1,
} from '@src/service/transport/ai-client/aiCustomJobDefinitionV1Schema'
import type { AiTriggerDefinitionId } from '@src/service/transport/ai-client/idSchemas'

export default class AiCustomJobsV1UiStore implements IUiStore {
  customJobs = new Collection<CodableAiCustomJobV1>()
  private disposeBag = new DisposeBag()

  private async handleFetchCustomJobs() {
    const { data } = await this.aiStore.automations.getCustomJobDefinitionsV1()
    this.customJobs.putBulk(data)
    return data
  }

  private fetchCustomJobsPromise = new StatefulPromise(
    this.handleFetchCustomJobs.bind(this),
  )

  private getCustomJobs() {
    return this.fetchCustomJobsPromise.run()
  }

  constructor(
    private aiStore: AiStore,
    private transport: Transport,
    private analytics: AnalyticsStore,
    private flags: IFlagsService,
  ) {
    makeAutoObservable(this, {}, { autoBind: true })

    if (this.flags.flags.webAiAgentAnnouncement) {
      void this.getCustomJobs().catch((e) => {
        logError(e)
      })
      this.handleSocketEvents()
    }
  }

  get jobsList() {
    return this.customJobs.list.sort((a, b) => {
      const aT = dayjs(a.updatedAt ?? a.createdAt).valueOf()
      const bT = dayjs(b.updatedAt ?? b.createdAt).valueOf()
      return bT - aT
    })
  }

  async createCustomJob(input: AiCustomJobDefinitionV1CreateInput) {
    const createdJob = await this.aiStore.automations.createCustomJobDefinitionV1(input)
    this.customJobs.put(createdJob)
    return createdJob
  }

  async updateCustomJob(update: AiCustomJobDefinitionV1UpdateInput) {
    const currentJob = this.customJobs.get(update.id)
    try {
      const updatedJob = await this.aiStore.automations.updateCustomJobDefinitionV1({
        ...update,
      })
      this.customJobs.put(updatedJob)
      return updatedJob
    } catch (e) {
      if (currentJob) {
        this.customJobs.put(currentJob)
      }
      throw e
    }
  }

  async deleteCustomJob(id: string) {
    const jobToDelete = this.customJobs.get(id)
    this.customJobs.delete(id)
    try {
      await this.aiStore.automations.deleteCustomJobDefinitionV1(id)
    } catch (e) {
      if (jobToDelete) {
        this.customJobs.put(jobToDelete)
      }
      throw e
    }
  }

  async addCustomJobToLocalTriggerDefinition({
    jobId,
    triggerDefinitionId,
    workflowDefinitionId,
    blockId,
  }: {
    jobId: CodableAiCustomJobV1['id']
    triggerDefinitionId: AiTriggerDefinitionId
    workflowDefinitionId: string
    blockId: string
  }) {
    await this.aiStore.automations.addCustomJobToLocalTriggerDefinition({
      jobId,
      triggerDefinitionId,
      workflowDefinitionId,
      blockId,
    })
  }

  private handleSocketEvents() {
    const subscription = this.transport.onNotificationData.subscribe((event) => {
      switch (event.type) {
        case 'agent-job-definition-create':
          return this.customJobs.put(event.definition)
        case 'agent-job-definition-update':
          return this.customJobs.put(event.definition)
        case 'agent-job-definition-delete':
          return this.customJobs.delete(event.id)
      }
    })

    this.disposeBag.add(subscription)
  }

  tearDown() {
    this.disposeBag.dispose()
  }
}
